import { deviceAuthorizationClient, inferAdditionalFields, organizationClient, twoFactorClient } from 'better-auth/client/plugins'
import { createAuthClient } from 'better-auth/react'

export const authClient = createAuthClient({
  basePath: '/api/auth',
  plugins: [
    organizationClient(),
    deviceAuthorizationClient(),
    // The sign-in form looks for `twoFactorRedirect` in the sign-in result
    // itself and routes to /two-factor, so no redirect option here.
    twoFactorClient(),
    // Mirrors `user.additionalFields` in apps/api/src/auth.ts.
    inferAdditionalFields({ user: { deletionScheduledFor: { type: 'date', required: false } } }),
  ],
})

export type SessionData = typeof authClient.$Infer.Session

/** Better Auth error codes → copy a reseller understands. */
export function authErrorMessage(error: { code?: string; message?: string; status?: number } | null | undefined): string {
  switch (error?.code) {
    case 'INVALID_EMAIL_OR_PASSWORD':
      return "That email and password don't match. Check both and try again."
    case 'USER_ALREADY_EXISTS':
    case 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL':
      return 'An account with this email already exists. Sign in instead?'
    case 'EMAIL_NOT_VERIFIED':
      return 'Confirm your email first. We just sent you a fresh link.'
    case 'SIGNUPS_INVITE_ONLY':
      return 'Nebueye is invite-only right now. Open the invitation link you were sent to create your account, or ask the owner of a Vault to invite you.'
    case 'SIGNUP_DISABLED':
      return "That Discord account isn't linked to a Nebueye account yet. Sign in with your email first, then link Discord in Settings."
    case 'PASSWORD_TOO_SHORT':
      return 'Use at least 10 characters for your password.'
    case 'INVALID_PASSWORD':
      return "That isn't your password. Check it and try again."
    case 'INVALID_CODE':
      return "That code didn't match. Codes change every 30 seconds; enter the current one."
    case 'INVALID_BACKUP_CODE':
      return "That backup code isn't valid. Each one works once."
    case 'TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE':
      return 'Too many wrong codes. Sign in again to get a new try.'
    case 'ACCOUNT_TEMPORARILY_LOCKED':
      return 'Too many wrong codes. Your account is locked for 15 minutes.'
    case 'INVALID_TWO_FACTOR_COOKIE':
      return 'Your sign-in timed out. Start again from the sign-in page.'
    case 'TOTP_ALREADY_ENABLED':
      return 'Two-factor authentication is already on for this account.'
    case 'MISSING_RESPONSE':
      return "The security check hasn't finished yet. Give it a second and try again."
    case 'VERIFICATION_FAILED':
      return "The security check didn't pass. Try again."
    case 'INVALID_TOKEN':
      return 'This link has expired or was already used. Request a new one.'
    case 'ORGANIZATION_ALREADY_EXISTS':
    case 'ORGANIZATION_SLUG_ALREADY_TAKEN':
      return 'That name is taken. Try a different one.'
    case 'INVALID_ORGANIZATION_NAME':
      // The server says which rule the name broke.
      return error?.message || 'Use 3–32 letters, numbers and spaces, with at least one letter.'
  }
  if (error?.status === 429) return 'Too many attempts. Wait a minute and try again.'
  return error?.message || 'Something went wrong. Try again in a moment.'
}
